// src/components/consultorio/AssinaturaTab.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Row, Col, Spinner, Alert, Image, Form } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import config from '../../config';
import { useEntidadeContext } from '../../EntidadeContext';

const AssinaturaTab = ({ consultaId }) => {
  const { currentEntidade } = useEntidadeContext();
  const sigPadRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedSignatureURL, setSavedSignatureURL] = useState(null);
  
  // Novos estados para os dados do signatário
  const [signatarioNome, setSignatarioNome] = useState('');
  const [signatarioCpf, setSignatarioCpf] = useState('');
  const [savedSignatarioNome, setSavedSignatarioNome] = useState('');
  const [savedSignatarioCpf, setSavedSignatarioCpf] = useState('');

  // Função para buscar a assinatura existente da consulta
  const fetchAssinatura = useCallback(async () => {
    if (!consultaId) return;
    setIsLoading(true);
    try {
      // BACKEND: Endpoint para buscar a assinatura de uma consulta específica.
      const response = await axios.get(`/api/consultas/${consultaId}/assinatura`);
      if (response.data) {
        if (response.data.assinatura_path) {
          setSavedSignatureURL(`${config.apiBaseURL}/storage/entidade/${currentEntidade.id}/assinaturas/${response.data.assinatura_path}`);
        } else {
          setSavedSignatureURL(null);
        }
        // Popula os dados do signatário salvo
        setSavedSignatarioNome(response.data.assinatura_signatario_nome || '');
        setSavedSignatarioCpf(response.data.assinatura_signatario_cpf || '');
      } else {
        setSavedSignatureURL(null);
        setSavedSignatarioNome('');
        setSavedSignatarioCpf('');
      }
    } catch (err) {
      // É normal não encontrar uma assinatura, então não tratamos como um erro fatal.
      console.log("Nenhuma assinatura encontrada para esta consulta.");
      setSavedSignatureURL(null);
      setSavedSignatarioNome('');
      setSavedSignatarioCpf('');
    } finally {
      setIsLoading(false);
    }
  }, [consultaId, currentEntidade.id]);

  // useEffect(() => {
  //   fetchAssinatura();
  // }, [fetchAssinatura]);

  // Limpa o canvas de assinatura
  const handleClear = () => {
    sigPadRef.current.clear();
    setError(null);
  };

  // Salva a assinatura
  const handleSave = async () => {
    if (sigPadRef.current.isEmpty()) {
      setError("Por favor, desenhe uma assinatura antes de salvar.");
      return;
    }
    // Validação para os novos campos
    if (!signatarioNome.trim() || !signatarioCpf.replace(/\D/g, '')) {
      setError("Por favor, preencha o Nome e o CPF do signatário.");
      return;
    }

    setIsSaving(true);
    setError(null);

    // Converte o canvas para uma imagem base64
    // O método getTrimmedCanvas() está causando um erro de importação interna da biblioteca.
    // Usar toDataURL() diretamente é uma alternativa funcional que resolve o problema.
    // A imagem não será cortada (terá espaço transparente ao redor), mas o salvamento funcionará.
    const signatureDataUrl = sigPadRef.current.toDataURL('image/png');

    try {
      // BACKEND: Endpoint para salvar a assinatura.
      const response = await axios.post(`/api/consultas/${consultaId}/assinatura`, {
        assinatura: signatureDataUrl, // Envia a imagem em base64
        entidade_id: currentEntidade.id,
        // Envia os novos campos
        signatario_nome: signatarioNome,
        signatario_cpf: signatarioCpf.replace(/\D/g, ''), // Envia apenas os números do CPF
      });

      if (response.status === 200 || response.status === 201) {
        // Atualiza a URL da imagem salva e limpa o canvas
        setSavedSignatureURL(`${config.apiBaseURL}/storage/entidade/${currentEntidade.id}/assinaturas/${response.data.assinatura_path}?t=${new Date().getTime()}`);
        setSavedSignatarioNome(response.data.assinatura_signatario_nome || '');
        setSavedSignatarioCpf(response.data.assinatura_signatario_cpf || '');
        sigPadRef.current.clear();
        setSignatarioNome('');
        setSignatarioCpf('');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Ocorreu um erro ao salvar a assinatura.";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-center"><Spinner animation="border" /></div>;
  }

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Row>
        {/* Coluna para a assinatura atual */}
        <Col md={6} className="border-end">
          <h5>Assinatura Digital do Paciente</h5>
          <p className="text-muted">Desenhe no campo abaixo para assinar digitalmente.</p>
          <div className="signature-container">
            <SignatureCanvas
              ref={sigPadRef}
              penColor='black'
              canvasProps={{ className: 'signature-canvas' }}
            />
          </div>
          {/* Novos campos para Nome e CPF */}
          <Form>
            <Row className="mt-3">
                <Col md={12}>
                    <Form.Group controlId="signatarioNome">
                        <Form.Label>Nome do Signatário:</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={signatarioNome}
                            onChange={(e) => setSignatarioNome(e.target.value)}
                            placeholder="Nome completo"
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col md={12}>
                    <Form.Group controlId="signatarioCpf">
                        <Form.Label>CPF do Signatário:</Form.Label>
                        <InputMask
                            mask="999.999.999-99"
                            value={signatarioCpf}
                            onChange={(e) => setSignatarioCpf(e.target.value)}
                        >
                            {(inputProps) => <Form.Control {...inputProps} type="text" placeholder="___.___.___-__" />}
                        </InputMask>
                    </Form.Group>
                </Col>
            </Row>
          </Form>
          <div className="mt-3">
            <Button variant="secondary" onClick={handleClear} className="me-2">
              Limpar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar Assinatura'}
            </Button>
          </div>
        </Col>

        {/* Coluna para a assinatura já salva */}
        <Col md={6}>
          <h5>Assinatura Salva</h5>
          {savedSignatureURL ? (
            <div>
              <Image src={savedSignatureURL} fluid thumbnail />
              {/* Exibe os dados do signatário salvo */}
              {savedSignatarioNome && (
                <div className="mt-2 border-top pt-2">
                    <strong>Signatário:</strong> {savedSignatarioNome} <br />
                    <strong>CPF:</strong> {savedSignatarioCpf}
                </div>
              )}
              <small className="text-muted d-block mt-2">Esta é a assinatura registrada para esta consulta.</small>
            </div>
          ) : (
            <Alert variant="light">Nenhuma assinatura foi salva para esta consulta ainda.</Alert>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AssinaturaTab;
